import { Button, useToast } from '@citizen-walk/shared-ui';
import {
  SURVEY_OTHER_VALUE,
  formatErrorForToast,
  fromUnknownError,
  type Survey,
  type SurveyAnswerValue,
  type SurveyParticipantSnapshot,
  type SurveyQuestion,
  type SurveyQuestionCheckbox,
  type SurveyQuestionLinearScale,
  type SurveyQuestionLongText,
  type SurveyQuestionNumber,
  type SurveyQuestionRadio,
  type SurveyQuestionRating,
  type SurveyQuestionShortText,
  type SurveyResponse,
} from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { useOnlineStatus } from '../shell';
import {
  EMPTY_SURVEY_PARTICIPANT_PROFILE,
  type SurveyParticipantProfile,
} from './participantSnapshot';
import { useSurveyResponse } from './useSurveyResponse';

/**
 * アンケート回答 form (Sprint T、user 第 16 弾)。
 *
 * 10 種類の質問 widget + validation + submit を全部担う。各質問種別の widget は
 * 同ファイル内に inline component として整理 (旧 v1 SurveyForm.tsx 踏襲、docs/97 §3 L3)。
 *
 * 旧との差分:
 *  - 旧 SDK ベース → v2 では `useSurveyResponse` hook 経由 (Firestore setDoc + state 管理)
 *  - 旧の「途中保存」(draft 状態) は本 sprint scope 外 (一発 submit のみ)
 *  - 旧 v1 のキャンセル時 confirm dialog は維持 (回答途中破棄防止)
 *
 * validation rule:
 *  - required: 空回答は「必須項目です」
 *  - short_text / long_text: maxLength 超過は「N 文字以内で入力してください」
 *  - checkbox: minSelected / maxSelected 違反は「N 件以上選択してください」/「N 件以下にしてください」
 *  - number: min / max 違反は「N 以上で入力してください」/「N 以下で入力してください」
 *  - radio / dropdown / linear_scale / rating / date / time は required check のみ
 */

export interface SurveyFormProps {
  survey: Survey;
  existingResponse: SurveyResponse | null;
  uid: string;
  districtId: string;
  /** 回答者の表示名 (participantSnapshot.nickname に焼込)。 */
  participantNickname: string;
  /**
   * 回答者のプロフィールと参加セッション (participantSnapshot のニックネーム以外に焼込)。
   * 省略時は空 (旧挙動)。
   */
  participantProfile?: SurveyParticipantProfile;
  /** submit 完了後の callback (toast はこの component 内で出す)。 */
  onSubmitted: () => void;
  /** キャンセル callback (modal 閉じる等)。 */
  onCancel: () => void;
}

/**
 * 質問 ID → 回答値の map (component 内部 state、validation pass 後に SurveyAnswerValue に変換)。
 *
 * 中間 form 表現は SurveyAnswerValue ではなく緩い型 (string / string[] / number) のまま管理し、
 * submit 時に `toAnswerValue` で正規化する。これにより radio の「その他」入力欄のような複合 state を
 * 1 つの map で扱える。
 */
type FormValue =
  | { kind: 'text'; value: string }
  | { kind: 'choice'; value: string; otherText: string }
  | { kind: 'choices'; values: string[]; otherText: string }
  | { kind: 'number'; raw: string }
  | { kind: 'date'; value: string }
  | { kind: 'time'; value: string };

type FormValues = Record<string, FormValue>;

/** 「その他」(記入欄付き) を選んだときの保存値 (= 文言「その他」、回答は選択肢の文言で保存する)。 */
const OTHER_VALUE = SURVEY_OTHER_VALUE;

/** 既存 response から初期 form state を構築 (新規時は空 default)。 */
function buildInitialValues(survey: Survey, existingResponse: SurveyResponse | null): FormValues {
  const out: FormValues = {};
  for (const q of survey.questions) {
    const a = existingResponse?.answers[q.id];
    switch (q.type) {
      case 'short_text':
      case 'long_text':
        out[q.id] = {
          kind: 'text',
          value: a && a.type === 'text' ? a.value : '',
        };
        break;
      case 'radio':
      case 'dropdown':
        if (a && a.type === 'choice') {
          // radio の「その他」は otherText 付き
          if (q.type === 'radio' && q.allowOther && a.value === OTHER_VALUE) {
            out[q.id] = { kind: 'choice', value: OTHER_VALUE, otherText: a.otherText ?? '' };
          } else {
            out[q.id] = { kind: 'choice', value: a.value, otherText: a.otherText ?? '' };
          }
        } else {
          out[q.id] = { kind: 'choice', value: '', otherText: '' };
        }
        break;
      case 'checkbox':
        if (a && a.type === 'choices') {
          out[q.id] = {
            kind: 'choices',
            values: Array.isArray(a.values) ? a.values : [],
            otherText: a.otherText ?? '',
          };
        } else {
          out[q.id] = { kind: 'choices', values: [], otherText: '' };
        }
        break;
      case 'linear_scale':
      case 'rating':
      case 'number':
        if (a && a.type === 'number') {
          out[q.id] = { kind: 'number', raw: String(a.value) };
        } else {
          out[q.id] = { kind: 'number', raw: '' };
        }
        break;
      case 'date':
        out[q.id] = { kind: 'date', value: a && a.type === 'date' ? a.value : '' };
        break;
      case 'time':
        out[q.id] = { kind: 'time', value: a && a.type === 'time' ? a.value : '' };
        break;
    }
  }
  return out;
}

/** form value → SurveyAnswerValue。validation pass 後に呼ぶ。 */
function toAnswerValue(q: SurveyQuestion, v: FormValue): SurveyAnswerValue | null {
  switch (q.type) {
    case 'short_text':
    case 'long_text':
      if (v.kind !== 'text') return null;
      if (v.value === '') return null;
      return { type: 'text', value: v.value };
    case 'radio':
    case 'dropdown':
      if (v.kind !== 'choice') return null;
      if (v.value === '') return null;
      if (q.type === 'radio' && q.allowOther && v.value === OTHER_VALUE) {
        return { type: 'choice', value: OTHER_VALUE, otherText: v.otherText };
      }
      return { type: 'choice', value: v.value };
    case 'checkbox':
      if (v.kind !== 'choices') return null;
      if (v.values.length === 0) return null;
      if (q.allowOther && v.values.includes(OTHER_VALUE)) {
        return { type: 'choices', values: v.values, otherText: v.otherText };
      }
      return { type: 'choices', values: v.values };
    case 'linear_scale':
    case 'rating':
    case 'number': {
      if (v.kind !== 'number') return null;
      if (v.raw === '') return null;
      const n = Number(v.raw);
      if (!Number.isFinite(n)) return null;
      return { type: 'number', value: n };
    }
    case 'date':
      if (v.kind !== 'date') return null;
      if (v.value === '') return null;
      return { type: 'date', value: v.value };
    case 'time':
      if (v.kind !== 'time') return null;
      if (v.value === '') return null;
      return { type: 'time', value: v.value };
  }
}

/** 質問単位の validation。エラーがあれば文字列を、無ければ null を返す (pure)。 */
export function validateQuestion(q: SurveyQuestion, v: FormValue | undefined): string | null {
  if (!v) {
    return q.required ? '必須項目です' : null;
  }
  switch (q.type) {
    case 'short_text':
    case 'long_text': {
      if (v.kind !== 'text') return null;
      if (q.required && v.value.trim() === '') return '必須項目です';
      const max = (q as SurveyQuestionShortText | SurveyQuestionLongText).maxLength;
      if (typeof max === 'number' && v.value.length > max) {
        return `${max} 文字以内で入力してください`;
      }
      return null;
    }
    case 'radio':
    case 'dropdown': {
      if (v.kind !== 'choice') return null;
      if (q.required && v.value === '') return '必須項目です';
      if (q.type === 'radio') {
        const qr = q as SurveyQuestionRadio;
        if (qr.allowOther && v.value === OTHER_VALUE && q.required && v.otherText.trim() === '') {
          return '「その他」の内容を入力してください';
        }
      }
      return null;
    }
    case 'checkbox': {
      if (v.kind !== 'choices') return null;
      const qc = q as SurveyQuestionCheckbox;
      if (q.required && v.values.length === 0) return '必須項目です';
      if (typeof qc.minSelected === 'number' && v.values.length < qc.minSelected) {
        return `${qc.minSelected} 件以上選択してください`;
      }
      if (typeof qc.maxSelected === 'number' && v.values.length > qc.maxSelected) {
        return `${qc.maxSelected} 件以下にしてください`;
      }
      if (
        qc.allowOther &&
        v.values.includes(OTHER_VALUE) &&
        q.required &&
        v.otherText.trim() === ''
      ) {
        return '「その他」の内容を入力してください';
      }
      return null;
    }
    case 'linear_scale': {
      if (v.kind !== 'number') return null;
      if (q.required && v.raw === '') return '必須項目です';
      if (v.raw === '') return null;
      const n = Number(v.raw);
      const ql = q as SurveyQuestionLinearScale;
      if (!Number.isFinite(n)) return '数値を入力してください';
      if (n < ql.min) return `${ql.min} 以上で入力してください`;
      if (n > ql.max) return `${ql.max} 以下で入力してください`;
      return null;
    }
    case 'rating': {
      if (v.kind !== 'number') return null;
      if (q.required && v.raw === '') return '必須項目です';
      if (v.raw === '') return null;
      const n = Number(v.raw);
      const qr = q as SurveyQuestionRating;
      if (!Number.isFinite(n)) return '数値を入力してください';
      if (n < 1 || n > qr.max) return `1〜${qr.max} の範囲で選択してください`;
      return null;
    }
    case 'number': {
      if (v.kind !== 'number') return null;
      if (q.required && v.raw === '') return '必須項目です';
      if (v.raw === '') return null;
      const n = Number(v.raw);
      const qn = q as SurveyQuestionNumber;
      if (!Number.isFinite(n)) return '数値を入力してください';
      if (typeof qn.min === 'number' && n < qn.min) return `${qn.min} 以上で入力してください`;
      if (typeof qn.max === 'number' && n > qn.max) return `${qn.max} 以下で入力してください`;
      return null;
    }
    case 'date': {
      if (v.kind !== 'date') return null;
      if (q.required && v.value === '') return '必須項目です';
      return null;
    }
    case 'time': {
      if (v.kind !== 'time') return null;
      if (q.required && v.value === '') return '必須項目です';
      return null;
    }
  }
}

/** 全質問の validation。 questionId → errorMessage の map (no error な質問は含まない)。 */
export function validateAll(survey: Survey, values: FormValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of survey.questions) {
    const err = validateQuestion(q, values[q.id]);
    if (err) out[q.id] = err;
  }
  return out;
}

// ============== Question widgets (file-internal components) ==============

interface WidgetProps<Q extends SurveyQuestion = SurveyQuestion> {
  question: Q;
  value: FormValue;
  onChange: (next: FormValue) => void;
  disabled: boolean;
}

function ShortTextWidget({
  question,
  value,
  onChange,
  disabled,
}: WidgetProps<SurveyQuestionShortText>) {
  const v = value.kind === 'text' ? value.value : '';
  return (
    <input
      type="text"
      value={v}
      onChange={(e) => onChange({ kind: 'text', value: e.target.value })}
      placeholder={question.placeholder}
      maxLength={question.maxLength}
      disabled={disabled}
      className="min-h-touch px-3 border border-slate-300 rounded text-sm w-full"
    />
  );
}

function LongTextWidget({
  question,
  value,
  onChange,
  disabled,
}: WidgetProps<SurveyQuestionLongText>) {
  const v = value.kind === 'text' ? value.value : '';
  return (
    <textarea
      value={v}
      onChange={(e) => onChange({ kind: 'text', value: e.target.value })}
      placeholder={question.placeholder}
      maxLength={question.maxLength}
      rows={4}
      disabled={disabled}
      className="px-3 py-2 border border-slate-300 rounded text-sm w-full"
    />
  );
}

function RadioWidget({ question, value, onChange, disabled }: WidgetProps<SurveyQuestionRadio>) {
  const selected = value.kind === 'choice' ? value.value : '';
  const otherText = value.kind === 'choice' ? value.otherText : '';
  return (
    <div className="grid gap-1.5">
      {question.options.map((opt) => (
        <label key={opt.label} className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="radio"
            name={question.id}
            value={opt.label}
            checked={selected === opt.label}
            onChange={() => onChange({ kind: 'choice', value: opt.label, otherText })}
            disabled={disabled}
          />
          <span>{opt.label}</span>
        </label>
      ))}
      {question.allowOther && (
        <>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="radio"
              name={question.id}
              value={OTHER_VALUE}
              checked={selected === OTHER_VALUE}
              onChange={() => onChange({ kind: 'choice', value: OTHER_VALUE, otherText })}
              disabled={disabled}
            />
            <span>その他</span>
          </label>
          {selected === OTHER_VALUE && (
            <input
              type="text"
              value={otherText}
              onChange={(e) =>
                onChange({ kind: 'choice', value: OTHER_VALUE, otherText: e.target.value })
              }
              placeholder="その他の内容"
              disabled={disabled}
              aria-label="その他の内容"
              className="ml-6 min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          )}
        </>
      )}
    </div>
  );
}

function DropdownWidget({ question, value, onChange, disabled }: WidgetProps) {
  if (question.type !== 'dropdown') return null;
  const selected = value.kind === 'choice' ? value.value : '';
  return (
    <select
      value={selected}
      onChange={(e) => onChange({ kind: 'choice', value: e.target.value, otherText: '' })}
      disabled={disabled}
      className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
    >
      <option value="">— 選択してください —</option>
      {question.options.map((opt) => (
        <option key={opt.label} value={opt.label}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function CheckboxWidget({
  question,
  value,
  onChange,
  disabled,
}: WidgetProps<SurveyQuestionCheckbox>) {
  const selected = value.kind === 'choices' ? value.values : [];
  const otherText = value.kind === 'choices' ? value.otherText : '';
  function toggle(label: string) {
    const next = selected.includes(label)
      ? selected.filter((v) => v !== label)
      : [...selected, label];
    onChange({ kind: 'choices', values: next, otherText });
  }
  return (
    <div className="grid gap-1.5">
      {question.options.map((opt) => (
        <label key={opt.label} className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={selected.includes(opt.label)}
            onChange={() => toggle(opt.label)}
            disabled={disabled}
          />
          <span>{opt.label}</span>
        </label>
      ))}
      {question.allowOther && (
        <>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={selected.includes(OTHER_VALUE)}
              onChange={() => toggle(OTHER_VALUE)}
              disabled={disabled}
            />
            <span>その他</span>
          </label>
          {selected.includes(OTHER_VALUE) && (
            <input
              type="text"
              value={otherText}
              onChange={(e) =>
                onChange({ kind: 'choices', values: selected, otherText: e.target.value })
              }
              placeholder="その他の内容"
              disabled={disabled}
              aria-label="その他の内容"
              className="ml-6 min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          )}
        </>
      )}
    </div>
  );
}

function LinearScaleWidget({
  question,
  value,
  onChange,
  disabled,
}: WidgetProps<SurveyQuestionLinearScale>) {
  // old2 逐語: linear_scale は range slider ではなく「離散値の radio 列」で表示する。
  // min〜max を step 刻みで列挙し、各値を 1 つの radio として縦ラベル付きで並べる。
  const cur = value.kind === 'number' && value.raw !== '' ? Number(value.raw) : undefined;
  const step = question.step ?? 1;
  const values: number[] = [];
  for (let v = question.min; v <= question.max; v += step) values.push(v);
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {question.minLabel && (
          <span className="text-xs text-slate-500 shrink-0">{question.minLabel}</span>
        )}
        <div className="flex flex-wrap gap-3 flex-1 justify-center">
          {values.map((v) => (
            <label key={v} className="flex flex-col items-center cursor-pointer text-xs">
              <input
                type="radio"
                name={question.id}
                checked={cur === v}
                onChange={() => onChange({ kind: 'number', raw: String(v) })}
                disabled={disabled}
                className="w-4 h-4"
              />
              <span className="mt-0.5">{v}</span>
            </label>
          ))}
        </div>
        {question.maxLabel && (
          <span className="text-xs text-slate-500 shrink-0">{question.maxLabel}</span>
        )}
      </div>
    </div>
  );
}

function RatingWidget({ question, value, onChange, disabled }: WidgetProps<SurveyQuestionRating>) {
  const current = value.kind === 'number' && value.raw !== '' ? Number(value.raw) : 0;
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label={question.title}>
      {Array.from({ length: question.max }, (_, i) => i + 1).map((n) => {
        const filled = n <= current;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange({ kind: 'number', raw: String(n) })}
            disabled={disabled}
            aria-label={`${n} 段階目`}
            aria-pressed={filled}
            className={`text-2xl leading-none min-h-touch min-w-touch px-1 ${
              filled ? 'text-warning-600' : 'text-slate-300'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {filled ? '★' : '☆'}
          </button>
        );
      })}
      {current > 0 && (
        <button
          type="button"
          onClick={() => onChange({ kind: 'number', raw: '' })}
          disabled={disabled}
          className="ml-2 text-xs text-slate-500 hover:text-slate-700 underline bg-transparent border-none p-0 cursor-pointer disabled:opacity-50"
        >
          クリア
        </button>
      )}
    </div>
  );
}

function DateWidget({ value, onChange, disabled }: WidgetProps) {
  const v = value.kind === 'date' ? value.value : '';
  return (
    <input
      type="date"
      value={v}
      onChange={(e) => onChange({ kind: 'date', value: e.target.value })}
      disabled={disabled}
      className="min-h-touch px-3 border border-slate-300 rounded text-sm"
    />
  );
}

function TimeWidget({ value, onChange, disabled }: WidgetProps) {
  const v = value.kind === 'time' ? value.value : '';
  return (
    <input
      type="time"
      value={v}
      onChange={(e) => onChange({ kind: 'time', value: e.target.value })}
      disabled={disabled}
      className="min-h-touch px-3 border border-slate-300 rounded text-sm"
    />
  );
}

function NumberWidget({ question, value, onChange, disabled }: WidgetProps<SurveyQuestionNumber>) {
  const raw = value.kind === 'number' ? value.raw : '';
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={raw}
        onChange={(e) => onChange({ kind: 'number', raw: e.target.value })}
        min={question.min}
        max={question.max}
        step={question.step}
        disabled={disabled}
        className="min-h-touch px-3 border border-slate-300 rounded text-sm w-32"
      />
      {question.unit && <span className="text-xs text-slate-500">{question.unit}</span>}
    </div>
  );
}

function QuestionWidget(props: WidgetProps) {
  const { question } = props;
  switch (question.type) {
    case 'short_text':
      return <ShortTextWidget {...(props as WidgetProps<SurveyQuestionShortText>)} />;
    case 'long_text':
      return <LongTextWidget {...(props as WidgetProps<SurveyQuestionLongText>)} />;
    case 'radio':
      return <RadioWidget {...(props as WidgetProps<SurveyQuestionRadio>)} />;
    case 'dropdown':
      return <DropdownWidget {...props} />;
    case 'checkbox':
      return <CheckboxWidget {...(props as WidgetProps<SurveyQuestionCheckbox>)} />;
    case 'linear_scale':
      return <LinearScaleWidget {...(props as WidgetProps<SurveyQuestionLinearScale>)} />;
    case 'rating':
      return <RatingWidget {...(props as WidgetProps<SurveyQuestionRating>)} />;
    case 'date':
      return <DateWidget {...props} />;
    case 'time':
      return <TimeWidget {...props} />;
    case 'number':
      return <NumberWidget {...(props as WidgetProps<SurveyQuestionNumber>)} />;
  }
}

// ============== Main component ==============

export function SurveyForm({
  survey,
  existingResponse,
  uid,
  districtId,
  participantNickname,
  participantProfile = EMPTY_SURVEY_PARTICIPANT_PROFILE,
  onSubmitted,
  onCancel,
}: SurveyFormProps) {
  const { toast } = useToast();
  const { submitResponse, state: respState } = useSurveyResponse(districtId, survey.id, uid);
  // Phase 2 層1 (docs/03 §7.5-3 / §7.5-8): オフラインでも送信可 (outbox に保留 → online 復帰で自動送信)。
  // isOnline は「予約案内 box + toast(info)」の出し分けにのみ使い、送信抑制には使わない。
  const isOnline = useOnlineStatus();
  const [values, setValues] = useState<FormValues>(() =>
    buildInitialValues(survey, existingResponse),
  );
  const [showErrors, setShowErrors] = useState(false);

  const errors = useMemo(() => validateAll(survey, values), [survey, values]);
  const hasErrors = Object.keys(errors).length > 0;
  const isSubmitting = respState.status === 'submitting' || respState.submitting;

  // status guard: published 以外は読取専用扱い (rules 経由でも write 試行は無駄なので UI でブロック)。
  // closed は admin 集計閲覧 phase なので参加者は新規回答不可、既存編集も不可 (旧 v1 と同 UX)。
  const writable = survey.status === 'published';
  // allowEdit=false かつ既存 response あり → 編集ロック (rules 側未 enforce、UI 責務)。
  const editLocked = !survey.allowEdit && existingResponse !== null;

  function setValue(qid: string, next: FormValue) {
    setValues((prev) => ({ ...prev, [qid]: next }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!writable) {
      toast('error', 'このアンケートは現在受付けていません');
      return;
    }
    if (editLocked) {
      toast('error', 'このアンケートは編集できません');
      return;
    }
    // Phase 2 層1 (docs/03 §7.5-3 / §7.5-8): オフラインでも outbox に保留 → online 復帰時に自動送信。
    // 旧 old2 の「オフライン送信抑制」は廃止 (耐久冪等 outbox により安全に保留できるため)。
    if (hasErrors) {
      setShowErrors(true);
      toast('error', '入力内容を確認してください');
      return;
    }
    // form values → answers Record
    const answers: Record<string, SurveyAnswerValue> = {};
    for (const q of survey.questions) {
      const v = values[q.id];
      if (!v) continue;
      const av = toAnswerValue(q, v);
      if (av) answers[q.id] = av;
    }
    const snapshot: SurveyParticipantSnapshot = {
      ...participantProfile,
      nickname: participantNickname,
    };
    try {
      await submitResponse(answers, snapshot, survey.version);
      if (!isOnline) {
        // outbox に保留済 (online 復帰時に自動送信)。
        toast('info', 'オフラインのため送信を予約しました。オンライン復帰時に自動送信されます。');
      } else {
        // old2 逐語: 新規送信完了時は感謝文言を含める。更新時は「回答を更新しました」。
        toast(
          'success',
          existingResponse
            ? '回答を更新しました'
            : '回答を送信しました。ご協力ありがとうございました。',
        );
      }
      onSubmitted();
    } catch (err) {
      // Sprint BF Part 5: AppError 階層 (RateLimit / Permission 等) を sub-class prefix 付きで表示。
      toast('error', `送信失敗: ${formatErrorForToast(fromUnknownError(err))}`);
    }
  }

  function handleCancel() {
    if (isSubmitting) return;
    // 簡易 dirty check: 既存値と現在値が異なる場合のみ confirm
    const initial = buildInitialValues(survey, existingResponse);
    const dirty = JSON.stringify(initial) !== JSON.stringify(values);
    if (dirty && !confirm('入力内容を破棄してよろしいですか?')) return;
    onCancel();
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      <div>
        <h3 className="text-lg font-bold text-slate-900">{survey.title || '(無題)'}</h3>
        {survey.description && (
          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{survey.description}</p>
        )}
        {!writable && (
          <p className="text-xs text-warning-700 mt-2">
            ⚠ このアンケートは現在 {survey.status === 'draft' ? '下書き' : '締切'} 状態です。
          </p>
        )}
        {editLocked && (
          <p className="text-xs text-warning-700 mt-2">
            🔒 編集不可のアンケートです (既に回答済み)。
          </p>
        )}
      </div>

      {/* Phase 2 層1: オフラインでも送信可 (outbox に保留 → online 復帰時に自動送信)。 */}
      {!isOnline && (
        <div
          className="p-2 bg-warning-50 border border-warning-300 rounded text-sm text-warning-800"
          data-testid="survey-offline-warning"
        >
          現在オフラインです。送信内容は保存され、オンラインに戻ると自動で送信されます。
        </div>
      )}

      {survey.questions.length === 0 && (
        <p className="text-sm text-slate-500">質問が設定されていません。</p>
      )}

      {survey.questions.map((q, idx) => {
        const err = errors[q.id];
        const showErr = showErrors && err;
        const value = values[q.id];
        if (!value) return null;
        const disabled = !writable || editLocked || isSubmitting;
        return (
          <div key={q.id} className="border border-slate-200 rounded-lg p-3 bg-white">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 shrink-0">
                #{idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">
                  {q.title || '(質問文未入力)'}
                  {q.required && <span className="ml-1 text-danger-600">*</span>}
                </p>
                {q.description && (
                  <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">
                    {q.description}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-2">
              <QuestionWidget
                question={q}
                value={value}
                onChange={(next) => setValue(q.id, next)}
                disabled={disabled}
              />
            </div>
            {showErr && <p className="text-[11px] text-danger-700 mt-1.5">{err}</p>}
          </div>
        );
      })}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button
          type="submit"
          disabled={!writable || editLocked || isSubmitting || (showErrors && hasErrors)}
        >
          {isSubmitting ? '送信中…' : existingResponse ? '更新する' : '送信する'}
        </Button>
      </div>
    </form>
  );
}
